import { ChevronRightIcon } from '@chakra-ui/icons';
import { Box, Button, Flex, HStack, SimpleGrid, Text } from '@chakra-ui/react';

import type { ImpersonationPreset } from '@revelio/auth';

interface ImpersonationPresetCardsProps {
  presets: ImpersonationPreset[];
  selectedPresetName: string | null;
  onSelectPreset: (preset: ImpersonationPreset) => void;
  onEditOverrides: () => void;
}

export function ImpersonationPresetCards({
  presets,
  selectedPresetName,
  onSelectPreset,
  onEditOverrides,
}: ImpersonationPresetCardsProps) {
  return (
    <Box>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
        {presets.map((preset) => {
          const isSelected = selectedPresetName === preset.name;
          return (
            <Flex
              key={preset.name}
              as="button"
              type="button"
              align="flex-start"
              gap={3}
              p={3}
              h="100%"
              border="1px solid"
              borderColor={isSelected ? 'blue.500' : 'gray.200'}
              borderRadius="md"
              bg={isSelected ? 'blue.50' : 'white'}
              boxShadow={
                isSelected ? '0 0 0 1px var(--chakra-colors-blue-500)' : 'none'
              }
              textAlign="left"
              cursor="pointer"
              transition="all 0.12s"
              _hover={{ borderColor: 'blue.400' }}
              onClick={() => onSelectPreset(preset)}
              data-testid={`preset-${preset.name.toLowerCase().replace(/\s+/g, '-')}`}
              aria-pressed={isSelected}
            >
              <Box
                w="16px"
                h="16px"
                mt={0.5}
                borderRadius="full"
                border="2px solid"
                borderColor={isSelected ? 'blue.500' : 'gray.300'}
                flexShrink={0}
                position="relative"
              >
                {isSelected && (
                  <Box
                    position="absolute"
                    inset="2px"
                    bg="blue.500"
                    borderRadius="full"
                  />
                )}
              </Box>
              <Box flex="1" minW={0}>
                <Text fontWeight="semibold" color="gray.800" fontSize="sm">
                  {preset.name}
                </Text>
                <Text fontSize="xs" color="gray.600" mt={0.5} noOfLines={2}>
                  {preset.description}
                </Text>
              </Box>
            </Flex>
          );
        })}
      </SimpleGrid>

      {selectedPresetName && (
        <HStack justify="flex-end" pt={2}>
          <Button
            variant="ghost"
            size="sm"
            colorScheme="blue"
            rightIcon={<ChevronRightIcon />}
            onClick={onEditOverrides}
            data-testid="edit-preset-overrides-button"
          >
            Edit overrides
          </Button>
        </HStack>
      )}
    </Box>
  );
}
